import { classNames } from "components/utils";
import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface FilterCardProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "title"
> {
  className?: string;
  buttonClassName?: string;
  icon: React.ReactNode;
  title: string;
  titleClass?: string;
  description?: string;
  descriptionClass?: string;
  filterApplied?: boolean;
  count?: number;
  hasTrailingAction?: boolean;
}

const FilterCard = React.forwardRef<HTMLButtonElement, FilterCardProps>(
  (
    {
      className,
      buttonClassName,
      icon,
      title,
      titleClass,
      description,
      descriptionClass,
      filterApplied,
      count,
      hasTrailingAction,
      type = "button",
      ...buttonProps
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      className={classNames(
        "relative flex cursor-pointer rounded-md text-left outline-none",
        "focus-visible:ring-2 focus-visible:ring-highlight focus-visible:ring-offset-2 focus-visible:ring-offset-primaryBg",
        filterApplied
          ? "bg-black/50 hover:bg-black/60"
          : "bg-zinc-800/50 hover:bg-zinc-800",
        "border animate-border",
        filterApplied ? "border-black" : "border-zinc-800",
        filterApplied
          ? "font-semibold text-highlight"
          : "text-secondary hover:text-white",
        buttonClassName ?? "",
      )}
      {...buttonProps}
    >
      <div className="flex flex-row items-center h-full">
        <div
          className={classNames(
            "py-2 pl-4",
            hasTrailingAction ? "pr-11" : "pr-4",
          )}
        >
          <div
            className={classNames(
              "flex flex-col min-h-[30px] justify-center",
              className ? className : "",
            )}
          >
            <div
              className={classNames(
                "flex flex-row items-center gap-2",
                filterApplied ? "text-highlight font-semibold" : "text-primary",
              )}
            >
              <div className="flex flex-row items-center gap-2">
                <div className="flex-0">{icon}</div>
                <span
                  className={classNames(
                    "text-sm whitespace-nowrap",
                    titleClass ? titleClass : "",
                  )}
                >
                  {title}
                </span>
              </div>
              {description && (
                <p
                  className={classNames(
                    "text-gray-500 dark:text-secondary text-xs",
                    descriptionClass ? descriptionClass : "",
                  )}
                >
                  {description}
                </p>
              )}
              {count !== undefined && (
                <span
                  className={classNames(
                    "ml-1 px-2 py-0.5 rounded-full text-xs font-semibold",
                    filterApplied
                      ? "bg-highlight text-black"
                      : "bg-zinc-700 text-secondary",
                  )}
                >
                  {count}
                </span>
              )}
            </div>
          </div>
        </div>
        <span className={`btnBefore`}></span>
        <span className={`btnAfter`}></span>
      </div>
    </button>
  ),
);

FilterCard.displayName = "FilterCard";

interface FilterRemoveButtonProps {
  filterName: string;
  onRemove: () => void;
}

export const FilterRemoveButton: React.FC<FilterRemoveButtonProps> = ({
  filterName,
  onRemove,
}) => (
  <button
    type="button"
    onClick={(event) => {
      const filterTrigger = event.currentTarget
        .previousElementSibling as HTMLButtonElement | null;
      onRemove();
      window.requestAnimationFrame(() => filterTrigger?.focus());
    }}
    className="absolute right-1 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-highlight transition-colors hover:bg-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-highlight focus-visible:ring-offset-2 focus-visible:ring-offset-primaryBg"
    aria-label={`Remove ${filterName} filter`}
  >
    <XMarkIcon aria-hidden="true" className="h-4 w-4" />
  </button>
);

export default FilterCard;
